import { useTranslation } from 'next-i18next'

import getUserTitle from '~/components/UserLevel/UserLevel.utils'
import useAvatarImageUrl from '~/dashboard/hooks/useAvatarImageUrl'
import useMasteryTrackPanelModel from '~/dashboard/hooks/useMasteryTrackPanelModel'
import { AvatarPng } from '~/dashboard/types/dashboard.types'
import { selectPoints } from '~/gamification/slices/gamification.slice'
import useCalculateLevel from '~/hooks/useCalculateLevel'
import useCurrentUser from '~/hooks/useCurrentUser'
import { getLeagueName } from '~/Leaderboard/utils/utils'
import { useGetFamilyMemberRewardsQuery } from '~/redux/apis/reward-api'
import { useAppSelector } from '~/redux/hooks'
import { useGetLeagueDetails } from '~/statistics-v2/hooks/useGetLeagueDetails'
import { IUser } from '~/types/user-info-types'

interface UserDetails {
  nickname: string
  avatarImageObject: AvatarPng
  userTitle: string
  userLevel: number
  gradeTitle?: string
  currentLeagueName?: string
  currentLeagueIconUrl?: string
  curentLeagueRank?: number
}

export function useGetUserDetails(isMobile?: boolean, familyMember?: IUser): UserDetails {
  const { t } = useTranslation('dashboard')
  const currentUser = useCurrentUser()
  const user = familyMember ? familyMember : currentUser
  const avatarImageObject = useAvatarImageUrl(familyMember?.id)

  const { title } = useMasteryTrackPanelModel()
  const { leagueIconUrl, currentLeague, userCurrentRank } = useGetLeagueDetails(familyMember)
  const currentLeagueName = t(`leaderboard:badges.${getLeagueName(currentLeague)}`)

  const nameLengthToShow = isMobile ? 11 : 16
  const nickname =
    user?.nickname && user.nickname.length > nameLengthToShow
      ? `${user.nickname.slice(0, nameLengthToShow - 2)}...`
      : user?.nickname || ''
  const points = useAppSelector(selectPoints)
  // Redux points belong to the logged-in user. In the parent view the level (and the title derived
  // from it) must come from the family member's own XP, or the child's card shows the parent's level.
  const { data: familyMemberRewards } = useGetFamilyMemberRewardsQuery(
    { familyMemberId: familyMember?.id },
    { skip: !familyMember },
  )
  const xp = familyMember ? familyMemberRewards?.xp || 0 : points || 0
  const userLevelData = useCalculateLevel(xp)
  const userTitle = getUserTitle(userLevelData.lvl)

  return {
    nickname,
    avatarImageObject,
    userTitle,
    userLevel: userLevelData.lvl,
    gradeTitle: title || undefined,
    currentLeagueName,
    currentLeagueIconUrl: leagueIconUrl,
    curentLeagueRank: userCurrentRank,
  }
}
